import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/outline';
import Navbar from '../components/common/Navbar';

const FREE_LICENSE_COUNT = Number(process.env.REACT_APP_FREE_LICENSE_COUNT) || 3;
const LICENSE_PRICE_PER_USER = Number(process.env.REACT_APP_LICENSE_PRICE_PER_USER) || 6;

const PricingPage: React.FC = () => {
    const navigate = useNavigate();

    const handleStartTrial = () => {
        navigate('/organization/signup?type=trial');
    };

    const handleStartPaid = () => {
        navigate('/organization/signup?type=paid');
    };

    const features = [
        'Secure password storage',
        'Team password sharing',
        'Access control & permissions',
        'Password strength monitoring',
        'Two-factor authentication',
        'Activity logs & reporting',
        'Browser extension support',
        'Priority customer support',
        'Custom categories & tags',
        'API access'
    ];

    const premiumHighlights = [
        `Scale past ${FREE_LICENSE_COUNT} seats`,
        'Unlimited password storage',
        'Advanced compliance reporting',
        'Priority customer support',
        'Dedicated success manager'
    ];

    return (
        <div className="bg-white">
            <Navbar />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="text-center mt-10">
                    <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
                        Pricing that grows with your team
                    </h1>
                    <p className="mt-4 text-xl text-gray-500">
                        Your first {FREE_LICENSE_COUNT} users are free forever. Add more seats anytime for ${LICENSE_PRICE_PER_USER.toFixed(2)} per user.
                    </p>
                </div>

                <div className="mt-16 grid lg:grid-cols-2 gap-8 lg:gap-12">
                    {/* Free Tier */}
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                        <div className="px-6 py-8">
                            <h3 className="text-2xl font-bold text-gray-900">Starter</h3>
                            <p className="mt-4 text-gray-500">First {FREE_LICENSE_COUNT} seats are free forever</p>
                            <p className="mt-8">
                                <span className="text-4xl font-extrabold text-gray-900">$0</span>
                                <span className="text-base font-medium text-gray-500">/up to {FREE_LICENSE_COUNT} users</span>
                            </p>
                            <button
                                onClick={handleStartTrial}
                                className="mt-8 block w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-md text-center"
                            >
                                Create Free Workspace
                            </button>
                        </div>
                        <div className="px-6 pt-6 pb-8 bg-gray-50">
                            <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
                                What's included
                            </h4>
                            <ul className="mt-4 space-y-4">
                                {features.map((feature) => (
                                    <li key={feature} className="flex items-start">
                                        <div className="flex-shrink-0">
                                            <CheckIcon className="h-6 w-6 text-green-500" />
                                        </div>
                                        <p className="ml-3 text-sm text-gray-700">{feature}</p>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Paid Plan */}
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-blue-500">
                        <div className="px-6 py-8">
                            <h3 className="text-2xl font-bold text-gray-900">Scale</h3>
                            <p className="mt-4 text-gray-500">
                                Need more than {FREE_LICENSE_COUNT} users? Only pay for the seats you need.
                            </p>
                            <p className="mt-8">
                                <span className="text-4xl font-extrabold text-gray-900">${LICENSE_PRICE_PER_USER.toFixed(2)}</span>
                                <span className="text-base font-medium text-gray-500">/user</span>
                            </p>
                            <p className="mt-2 text-sm text-gray-500">
                                When you upgrade, total seats (including the first {FREE_LICENSE_COUNT}) are billed per user.
                            </p>
                            <button
                                onClick={handleStartPaid}
                                className="mt-8 block w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-md text-center"
                            >
                                Get Started Now
                            </button>
                        </div>
                        <div className="px-6 pt-6 pb-8 bg-gray-50">
                            <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
                                Everything in free, plus
                            </h4>
                            <ul className="mt-4 space-y-4">
                                {premiumHighlights.map((highlight) => (
                                    <li key={highlight} className="flex items-start">
                                        <div className="flex-shrink-0">
                                            <CheckIcon className="h-6 w-6 text-green-500" />
                                        </div>
                                        <p className="ml-3 text-sm text-gray-700">{highlight}</p>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="mt-12 text-center">
                    <p className="text-base text-gray-500">
                        Questions? Contact our sales team at{' '}
                        <a href="mailto:sales@example.com" className="text-blue-600 hover:text-blue-500">
                            sales@example.com
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PricingPage; 